// Space channel slice is to take in and send out messages concerning in space events
// Taking in message will parse them and dissect their parts before
// sending out to the various handlers

import { createSlice } from "@reduxjs/toolkit";
import { selectSocket } from "./socketSlice";
import { Presence } from "phoenix";
import {
  initialiseSpace,
  handleRemoteShout,
  handleRemoteLeave,
  handleRemoteJoin,
  handlePersonMuted,
  handlePersonShoutable,
  handlePersonLocked,
  handleChangedRoleId,
  handleLeaveRoom,
  handleAllKicked,
  handleMembersMuted,
  handleMembersShoutable,
  handleMembersChatable,
  handleSpaceDeviceChanged,
  handleRemoteFocus,
  handleRemoteBuzzed,
  handleRemoteTalking,
  handleMembersLocked,
  handleRemotePinnedShare,
  handleSpaceLocked,
  handleKickedFromSpace,
  handleChatMsg,
  handleShare,
} from "slices/spaceSlice";
import { selectGuestInfo, setGeneralModal } from "slices/generalSlice";
import { handleMoveOther, handleMoves } from "space/spaceSignalling";
import { adjustID, unadjustID } from "helpers/efficeHelpers";
import { tryConnectToRoom } from "slices/mediaServerSlice";
import {
  receivedBoardAdd,
  receivedBoardClear,
  receivedBoardElements,
  receivedBoardErase,
  receivedBoardOCE,
  receivedBoardOCERequest,
  receivedBoardTease,
} from "space/spaceBoard";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

export const slice = createSlice({
  name: "spaceChannel",
  initialState: {
    channel: null,
    enterButtonLoading: false,
  },
  reducers: {
    setSpaceChannel: (state, action) => {
      state.channel = action.payload;
    },
    setEnterButtonLoading: (state, action) => {
      state.enterButtonLoading = action.payload;
    },
  },
});

export const { setSpaceChannel, setEnterButtonLoading } = slice.actions;

let spaceChannel = null;
export function getCurrentSpaceChannel() {
  return spaceChannel;
}
window.getSpaceChannel = getCurrentSpaceChannel;

export async function joinSpaceChannel(nodeId) {
  let socket = selectSocket();
  if (!socket) return;
  let joinPayload = { code: selectGuestInfo(getState())?.code };
  spaceChannel = socket.channel("space:" + unadjustID(nodeId), joinPayload);

  // TODO - finish the following
  // spaceChannel.on("space_recalibrate", (value) =>
  //   console.log("Room Recalibrate: " + JSON.stringify(value, null, 4))
  // );
  // spaceChannel.on("movement_recalibrate", (value) =>
  //   console.log("Room Recalibrate: " + JSON.stringify(value, null, 4))
  // );
  // spaceChannel.on("board_recalibrate", (value) =>
  //   console.log("Room Recalibrate: " + JSON.stringify(value, null, 4))
  // );

  // Board Messages
  spaceChannel.on("b_el", ({ o, v: { oce, elements } }) =>
    receivedBoardElements(o.toString(), {
      oce,
      elements: Object.values(elements),
    })
  );
  spaceChannel.on("b_t", ({ o, v }) => receivedBoardTease(o.toString(), v));
  spaceChannel.on("b_a", ({ o, v }) => receivedBoardAdd(o.toString(), v));
  spaceChannel.on("b_e", ({ o, v }) => receivedBoardErase(o.toString(), v));
  spaceChannel.on("b_c", ({ o }) => receivedBoardClear(o.toString()));
  spaceChannel.on("b_o", ({ o, v }) => receivedBoardOCE(o.toString(), v));
  spaceChannel.on("b_oreq", receivedBoardOCERequest);
  // following still just for board messages until video and board signalling get merged into server
  spaceChannel.on("share", ({ luid, value }) =>
    handleShare(luid.toString(), value)
  );

  // General Messages
  spaceChannel.on("new_room_url", ({ room_url, token }) =>
    tryConnectToRoom({ room_url, token })
  );
  spaceChannel.on("mvs", (payload) => handleMoves(payload));
  spaceChannel.on("moved_other", ({ luid, x, y }) =>
    handleMoveOther(luid.toString(), x, y)
  );
  spaceChannel.on("shout", ({ luid, value }) =>
    handleRemoteShout(luid.toString(), value)
  );
  spaceChannel.on("pinned_share", ({ luid, value }) =>
    handleRemotePinnedShare(luid.toString(), value)
  );
  spaceChannel.on("tk", ({ l, v }) => handleRemoteTalking(l.toString(), v));
  spaceChannel.on("focus", ({ luid, value }) =>
    handleRemoteFocus(luid.toString(), value)
  );
  spaceChannel.on("buzz", ({ luid, value }) =>
    handleRemoteBuzzed(luid.toString(), value)
  );
  spaceChannel.on("chat_msg", ({ luid, value }) =>
    handleChatMsg(luid.toString(), value)
  );
  spaceChannel.on("person_join", ({ person, x, y }) => {
    person.x = x;
    person.y = y;
    handleRemoteJoin(person);
  });
  spaceChannel.on("person_left", ({ luid }) =>
    handleRemoteLeave(luid.toString())
  );
  spaceChannel.on("person_muted", ({ luid, value }) =>
    handlePersonMuted(luid.toString(), value)
  );
  spaceChannel.on("person_shoutable", ({ luid, value }) =>
    handlePersonShoutable(luid.toString(), value)
  );
  spaceChannel.on("person_locked", ({ luid, value }) =>
    handlePersonLocked(luid.toString(), value)
  );
  spaceChannel.on("members_muted", ({ value }) => handleMembersMuted(value));
  spaceChannel.on("members_shoutable", ({ value }) =>
    handleMembersShoutable(value)
  );
  spaceChannel.on("members_chatable", ({ value }) =>
    handleMembersChatable(value)
  );
  spaceChannel.on("members_locked", ({ value }) => handleMembersLocked(value));
  spaceChannel.on("space_locked", ({ value }) => handleSpaceLocked(value));
  spaceChannel.on("changed_role_id", ({ luid, value }) =>
    handleChangedRoleId(luid.toString(), value)
  );
  spaceChannel.on("space_device_changed", ({ luid }) =>
    handleSpaceDeviceChanged(luid.toString())
  );
  spaceChannel.on("kicked_from_space", ({ luid }) =>
    handleKickedFromSpace(luid.toString())
  );
  spaceChannel.on("leave_room", handleLeaveRoom);
  spaceChannel.on("all_kicked", handleAllKicked);

  spaceChannel.onClose(() => {
    // spaceChannel = null;
    window.efs_debug && window.log("SPACE Channel CLOSING");
  });
  spaceChannel
    .join()
    .receive("ok", ({ space_state, pos, token }) => {
      window.efs_debug &&
        window.log("Successfully joined Channel - Space: " + nodeId);
      window.efs_debug && window.log({ space_state, pos });
      space_state = {
        ...space_state,
        space_id: adjustID(space_state.space_id),
      };
      window.efs_debug && window.log({ space_state, pos, token });

      initialiseSpace({ space_state, pos, token });
    })
    .receive("error", ({ reason }) => {
      window.efs_debug && window.log("failed join", reason);
      dispatch(
        setGeneralModal({
          title: "Failed to Enter this Room",
          content: reason,
        })
      );
      dispatch(setEnterButtonLoading(false));
      spaceChannel.leave();
    })
    .receive("timeout", () => {
      window.efs_debug && window.log("Networking issue. Still waiting...");
      dispatch(setEnterButtonLoading(false));
    });

  let presence = new Presence(spaceChannel);

  // detect if user has joined for the 1st time or from another tab/device
  presence.onJoin((id, current, newPres) => {
    if (!current)
      window.efs_debug &&
        window.log("user has entered for the first time", newPres);
    else window.efs_debug && window.log("user additional presence", newPres);
  });

  // detect if user has left from all tabs/devices, or is still present
  presence.onLeave((id, current, leftPres) => {
    if (current.metas.length === 0)
      window.efs_debug &&
        window.log("user has left from all devices", leftPres);
    else window.efs_debug && window.log("user left from a device", leftPres);
  });
  // receive presence data from server
  presence.onSync(() => {
    //displayUsers(presence.list());
  });
}

// Board Messages
export const sendAddToBoard = (owner, element) =>
  spaceChannel?.push("b_add", { o: parseInt(owner), v: element });
export const sendErasesFromBoard = (owner, element_id) =>
  spaceChannel?.push("b_ers", { o: parseInt(owner), v: element_id });
export const sendClearBoard = (owner) =>
  spaceChannel?.push("b_clr", parseInt(owner));
export const sendOthersCanEdit = (value) =>
  spaceChannel?.push("b_oce", { v: value });
// Board Subscriptions
export const sendMyWholeBoard = (elements) =>
  spaceChannel?.push("b_mwb", elements);
export const sendMyTease = (tease) => {
  spaceChannel?.push("b_mt", tease);
};
export const sendStopBoard = (luid) =>
  new Promise((res, rej) => {
    spaceChannel
      ?.push("b_stop", parseInt(luid))
      .receive("ok", res)
      .receive("error", rej);
  });
export const sendTeaseBoard = (luid) =>
  spaceChannel?.push("b_tease", parseInt(luid));
export const sendLiveBoard = (luid) =>
  new Promise((res, rej) => {
    spaceChannel
      ?.push("b_live", parseInt(luid))
      .receive("ok", ({ oce, elements }) => {
        res({ oce, elements: Object.values(elements) });
      })
      .receive("error", rej);
  });

// General Messages
export const sendMove = (newX, newY) => spaceChannel?.push("mv", [newX, newY]);
export const sendMovedOther = (x, y, luid) =>
  spaceChannel?.push("moved_other", { x, y, luid });
export const sendTalking = (value) => spaceChannel?.push("tk", { v: value });
export const sendShout = (value) => spaceChannel?.push("shout", { value });
export const sendShare = (value) => spaceChannel?.push("share", { value });
export const sendPinnedShare = (value) =>
  spaceChannel?.push("pinned_share", { value });
export const sendFocus = (value) => spaceChannel?.push("focus", { value });
export const sendBuzz = (luid) => spaceChannel?.push("buzz", { luid });

// Admin Controls
export const sendPersonMuted = (luid, value) =>
  spaceChannel?.push("person_muted", { luid, value });
export const sendPersonShoutable = (luid, value) =>
  spaceChannel?.push("person_shoutable", { luid, value });
export const sendPersonLocked = (luid, value) =>
  spaceChannel?.push("person_locked", { luid, value });
export const sendKickFromSpace = (luid) =>
  spaceChannel?.push("kick_from_space", { luid });
export const sendMembersMuted = (value) =>
  spaceChannel?.push("members_muted", { value });
export const sendMembersShoutable = (value) =>
  spaceChannel?.push("members_shoutable", { value });
export const sendMembersChatable = (value) =>
  spaceChannel?.push("members_chatable", { value });
export const sendMembersLocked = (value) =>
  spaceChannel?.push("members_locked", { value });
export const sendLockSpace = (value) =>
  spaceChannel?.push("lock_space", { value });
export const sendChatMsg = (value) => spaceChannel?.push("chat_msg", { value });

// Promise based leave, but with a delay to automatically resolve if time expires
export const leaveExistingSpaceChannel = async () =>
  new Promise((res) => {
    if (spaceChannel) {
      spaceChannel.leave().receive("ok", () => {
        window.efs_debug && window.log("Successfully left space channel 02");
        res();
        return;
      });
      setTimeout(() => {
        res();
        return;
      }, 3000);

      spaceChannel = null;
    } else window.efs_debug && window.log("No Space Channel Exists Yet");
  });

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
// export const selectSpaceChannel = () => spaceChannel;
export const selectEnterButtonLoading = (state) =>
  state.spaceChannel.enterButtonLoading;

export default slice.reducer;
